// src/pages/Exams/generate/types.ts

export type JobPhase =
  | 'queued' | 'fetching_chunks' | 'generating' | 'scoring'
  | 'review_pending' | 'saved' | 'failed' | 'cancelled';

export type DifficultyMode = 'mixed' | 'easy' | 'medium' | 'hard';
export type Difficulty = 'easy' | 'medium' | 'hard';

export interface ChapterDistributionEntry {
  chapter_id: number;
  count: number;
  difficulty_override: Difficulty | null;
  /** Next-exam prediction hints: concepts to cover, the predicted mix, last paper's stems to avoid. */
  concepts?: string[];
  difficulty_pct?: { easy: number; medium: number; hard: number };
  avoid?: string[];
}

export type PredictedTrend = 'up' | 'down' | 'flat' | 'due';
export type PredictedConcept = { name: string; status: 'recurring' | 'due' | 'recent'; weight: number };
export interface PredictedChapter {
  chapter_id: number;
  name: string;
  predicted: number;
  share: number;
  trend: PredictedTrend;
  confidence: 'high' | 'medium' | 'low';
  history: Array<{ year: number | null; count: number }>;
  difficulty: { easy: number; medium: number; hard: number } | null;
  concepts: PredictedConcept[];
  avoid: string[];
}
export interface NextExamPrediction {
  available: boolean;
  papers: number;
  years: Array<number | null>;
  total?: number;
  chapters: PredictedChapter[];
  difficulty: { easy: number; medium: number; hard: number } | null;
  types: Record<string, number>;
  backtest: { year: number; match_pct: number } | null;
  tagging: { tagged: number; total: number; running?: boolean };
}

export interface Blueprint {
  grade_id: number | null;
  subject_id: number | null;
  name: string;
  duration_min: number;
  total_questions: number;
  national_exam_id: number | null;
  difficulty_mode: DifficultyMode;
  difficulty_pct: { easy: number; medium: number; hard: number };
  chapter_distribution: ChapterDistributionEntry[];
  book_ids_included: number[];
  num_forms: number;
}

export interface QuestionDraft {
  form_label: string;
  chapter_id: number;
  question: string;
  choice_a: string;
  choice_b: string;
  choice_c: string;
  choice_d: string;
  ans: string;
  explanation: string;
  reference: string;
  difficulty: Difficulty | string;
  bloom_level: string;
  quality_score: number | null;
  flags: string[];
  quality_notes: string;
}

export interface FailedChapter {
  form_label: string;
  chapter_id: number;
  chapter_name: string;
  reason: 'no_source' | 'gemini_error' | 'timeout' | 'parse_failed'
    | 'ai_limit' | 'openrouter_invalid_key' | 'openrouter_daily_limit' | 'openrouter_busy';
}

export interface JobRow {
  id: number;
  phase: JobPhase;
  progress_total: number;
  progress_completed: number;
  progress_failed: number;
  current_label: string;
  output_questions: QuestionDraft[];
  failed_chapters: FailedChapter[];
  error_message: string;
  revision: number;
  blueprint: Blueprint;
  style_profile: Record<string, unknown>;
  exam_set_id: number | null;
  created_at: string;
  updated_at: string;
  /** When the worker picked the job up / finished it. Absent on older servers. */
  started_at?: string | null;
  completed_at?: string | null;
}

export interface NationalExam {
  id: number;
  name: string;
}

export interface NationalExamAggregate {
  insufficient_data?: boolean;
  chapter_counts?: Array<{ chapter_id: number; count: number }>;
  total_questions?: number;
  difficulty_pct?: { easy: number; medium: number; hard: number };
}

export interface ChapterRow {
  id: number;
  name: string;
  number?: number;
  /** The grades the chapter is taught in (chapters are grouped by grade). */
  school_grade_ids?: number[];
}

export interface BookRow {
  id: number;
  title: string;
  /** The book's grade(s), from the textbook list; used to preselect the exam grade's books. */
  grade?: string | null;
  school_grade_ids?: number[];
}

export interface SaveResponse {
  exam_set_id: number;
  exam_ids: Array<{ form_label: string; exam_id: number }>;
}

export const DEFAULT_BLUEPRINT: Blueprint = {
  grade_id: null,
  subject_id: null,
  name: '',
  duration_min: 60,
  total_questions: 30,
  national_exam_id: null,
  difficulty_mode: 'mixed',
  difficulty_pct: { easy: 40, medium: 40, hard: 20 },
  chapter_distribution: [],
  book_ids_included: [],
  num_forms: 1,
};

// ---- Plan C additions ----

export type RegenVariant = 'rephrase' | 'harder' | 'easier' | 'regenerate';

// Closed enum from spec §5.5
export type QuestionFlag =
  | 'ambiguous_question'
  | 'choice_ambiguity'
  | 'weak_distractors'
  | 'reference_mismatch'
  | 'multiple_correct';

export interface Template {
  id: number;
  name: string;
  blueprint: Blueprint;
  is_shared: boolean;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobHistoryRow {
  id: number;
  phase: JobPhase;
  progress_total: number;
  progress_completed: number;
  progress_failed: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  name: string;
  subject_name: string;
  duration_min: number | null;
  num_forms: number;
  exam_set: number | null;
  /** From the blueprint. Absent on older servers. */
  grade_name?: string;
  total_questions?: number | null;
  started_at?: string | null;
}
